import { yupResolver } from '@hookform/resolvers/yup';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { ReCaptcha } from '@/components/ReCaptcha';
import { CustomErrors } from '@/components/ServerErrors';
import { Typography } from '@/components/Typography';
import { commonMessages } from '@/messages/common';
import { serviceMessages } from '@/messages/service';
import { useRegionProvider } from '@/providers/RegionProvider';

import type { WarrantyExtensionSchema } from './schema';
import { warrantyExtensionSchema } from './schema';

import ReferenceHint from '@/assets/reference-and-serial-hint.png';

import { eventMessages } from '../../../messages';
import * as S from './WarrantyExtensionForm.styles';

export interface WarrantyExtensionFormProps {
  errorMessage: ReactNode;
  isProcessing?: boolean;
  onSubmit: SubmitHandler<WarrantyExtensionSchema>;
}

export const WarrantyExtensionForm = ({
  errorMessage = null,
  isProcessing,
  onSubmit,
}: WarrantyExtensionFormProps) => {
  const { formatMessage } = useIntl();
  const [isVerifyingRecaptcha, setIsVerifyingRecaptcha] = useState(false);
  const schema = warrantyExtensionSchema(formatMessage);
  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });
  const {
    query: { languageCode },
  } = useRegionProvider();

  const [captcha] = formCtx.watch(['captcha']);

  return (
    <S.Container>
      <CustomErrors message={errorMessage} />
      <FormProvider name='warrantyExtension' {...formCtx} onSubmit={onSubmit}>
        <S.FormWrapper>
          <InputField
            colorVariant='grey'
            disabled={isProcessing}
            name='rn'
            placeholder={formatMessage(serviceMessages.referenceNumber)}
            required
          />
          <InputField
            colorVariant='grey'
            disabled={isProcessing}
            name='sn'
            placeholder={formatMessage(serviceMessages.serialNumber)}
            required
          />
          <ReCaptcha
            languageCode={languageCode}
            name='captcha'
            onChange={setIsVerifyingRecaptcha}
          />
          <Flex justifyContent='center'>
            <LegacyButton
              data-testid='submitButton'
              disabled={isVerifyingRecaptcha || !captcha || isProcessing}
              isProcessing={isVerifyingRecaptcha || isProcessing}
              type='submit'
            >
              {formatMessage(commonMessages.submit)}
            </LegacyButton>
          </Flex>
        </S.FormWrapper>
      </FormProvider>
      <Typography color='blackOlive' mt={4} textAlign='center'>
        {formatMessage(eventMessages.findModeAndSn)}
      </Typography>
      <S.Image alt='' src={ReferenceHint} />
    </S.Container>
  );
};

WarrantyExtensionForm.displayName = 'WarrantyExtensionForm';
